/**
 * Admin Round Call curator. Lets ADMIN_EMAILS users pick exactly 20 players
 * for the shared Round Call on each matchday. The "Suggest 20" button seeds
 * the picks with the auto-generated engine output; admin then adds/removes
 * via the searchable browse table.
 *
 * Wired to:
 *   GET  /api/worldcup/admin/players
 *   GET  /api/worldcup/admin/matchday-call/:md/curation
 *   POST /api/worldcup/admin/matchday-call/:md/curation
 *   POST /api/worldcup/admin/matchday-call/:md/publish
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import {
    ArrowLeft, MagnifyingGlass, Plus, X, Sparkle, Check, CloudArrowUp,
} from "@phosphor-icons/react";
import AppShell from "../components/AppShell";
import { ACTIVE_FROM_MD, ACTIVE_ROUNDS } from "../lib/wcRounds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_PICKS = 20;
const ROUNDS = ACTIVE_ROUNDS.map((r) => r.n);
const POSITIONS = ["", "GKP", "DEF", "MID", "FWD"];

// Emergent Auth uses an http-only session cookie. axios needs withCredentials
// for cross-origin requests so the cookie is sent to the backend.
const AX = axios.create({ withCredentials: true });

export default function AdminRoundCall() {
    const [matchday, setMatchday] = useState(ACTIVE_FROM_MD);
    const [pickIds, setPickIds] = useState([]);            // selected fd_player_id[]
    const [suggested, setSuggested] = useState([]);        // ids from engine
    const [pickedDetails, setPickedDetails] = useState({}); // id -> player row
    const [search, setSearch] = useState("");
    const [position, setPosition] = useState("");
    const [otwOnly, setOtwOnly] = useState(false);
    const [sort, setSort] = useState("price_desc");
    const [browseRows, setBrowseRows] = useState([]);
    const [browseTotal, setBrowseTotal] = useState(0);
    const [loadingBrowse, setLoadingBrowse] = useState(false);
    const [loadingCuration, setLoadingCuration] = useState(true);
    const [publishing, setPublishing] = useState(false);
    const [permError, setPermError] = useState(null);
    const [publishedAt, setPublishedAt] = useState(null);

    // ---- load saved draft + suggestion seed when matchday changes -------
    useEffect(() => {
        let cancelled = false;
        async function load() {
            setLoadingCuration(true);
            try {
                const { data } = await AX.get(`${API}/worldcup/admin/matchday-call/${matchday}/curation`);
                if (cancelled) return;
                setPickIds(data.player_ids || []);
                setSuggested(data.suggested_player_ids || []);
                setPublishedAt(data.published_at);
                setPermError(null);
            } catch (e) {
                if (cancelled) return;
                if (e.response?.status === 401) setPermError("Not signed in — log in with an admin Google account.");
                else if (e.response?.status === 403) setPermError("Your account isn't whitelisted as admin.");
                else setPermError(e.response?.data?.detail || "Failed to load curation draft");
            } finally {
                if (!cancelled) setLoadingCuration(false);
            }
        }
        load();
        return () => { cancelled = true; };
    }, [matchday]);

    // ---- resolve picked player details (so we can show name + meta) -----
    useEffect(() => {
        const missing = pickIds.filter((id) => !pickedDetails[id]);
        if (missing.length === 0) return;
        // Pull rows for these ids via the public Round Call generate (cheap)
        AX.post(`${API}/worldcup/matchday-call/generate?matchday=${matchday}&regenerate=false`)
            .then(({ data }) => {
                const idx = {};
                (data.players || []).forEach((p) => { idx[p.playerId] = p; });
                if (missing.some((id) => idx[id])) {
                    setPickedDetails((prev) => ({ ...prev, ...idx }));
                }
            }).catch(() => { /* silent */ });
    }, [pickIds, matchday]);

    // ---- browse table (debounced) ---------------------------------------
    useEffect(() => {
        const t = setTimeout(() => {
            setLoadingBrowse(true);
            const params = new URLSearchParams({ limit: "60", sort });
            if (search.trim()) params.set("search", search.trim());
            if (position) params.set("position", position);
            if (otwOnly) params.set("one_to_watch", "true");
            AX.get(`${API}/worldcup/admin/players?${params.toString()}`).then(({ data }) => {
                setBrowseRows(data.players || []);
                setBrowseTotal(data.total || 0);
            }).catch(() => { setBrowseRows([]); setBrowseTotal(0); })
                .finally(() => setLoadingBrowse(false));
        }, 300);
        return () => clearTimeout(t);
    }, [search, position, otwOnly, sort]);

    // ---- helpers ---------------------------------------------------------
    const togglePick = (player) => {
        const id = player.playerId;
        if (!id) {
            toast.error(`${player.webName} has no FD crossref — can't add.`);
            return;
        }
        if (pickIds.includes(id)) {
            setPickIds(pickIds.filter((x) => x !== id));
            return;
        }
        if (pickIds.length >= MAX_PICKS) {
            toast.error(`Max ${MAX_PICKS} picks per round.`);
            return;
        }
        setPickIds([...pickIds, id]);
        setPickedDetails((prev) => ({ ...prev, [id]: player }));
    };

    const seedFromSuggestion = () => {
        if (suggested.length === 0) {
            toast.error("No suggestion available — try regenerate first.");
            return;
        }
        setPickIds(suggested.slice(0, MAX_PICKS));
        toast.success(`Seeded ${Math.min(suggested.length, MAX_PICKS)} picks from engine suggestion.`);
    };

    const clearPicks = () => setPickIds([]);

    const saveDraft = async () => {
        try {
            await AX.post(
                `${API}/worldcup/admin/matchday-call/${matchday}/curation`,
                { player_ids: pickIds },
            );
            toast.success(`Draft saved · ${pickIds.length} picks`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Save failed");
        }
    };

    const publish = async () => {
        if (pickIds.length === 0) { toast.error("Pick at least 1 player first."); return; }
        setPublishing(true);
        try {
            // Save first, then publish
            await AX.post(
                `${API}/worldcup/admin/matchday-call/${matchday}/curation`,
                { player_ids: pickIds },
            );
            const { data } = await AX.post(
                `${API}/worldcup/admin/matchday-call/${matchday}/publish`,
                {},
            );
            toast.success(`Published · ${data.playerCount} players live for MD${matchday}`);
            setPublishedAt(new Date().toISOString());
        } catch (e) {
            toast.error(e.response?.data?.detail || "Publish failed");
        } finally {
            setPublishing(false);
        }
    };

    const isPicked = (id) => pickIds.includes(id);

    // ---- derive picked rows from cache ----------------------------------
    const pickedRows = useMemo(() =>
        pickIds.map((id, i) => ({ id, idx: i + 1, p: pickedDetails[id] || { playerId: id, webName: `#${id}` } })),
    [pickIds, pickedDetails]);

    if (permError) {
        return (
            <AppShell>
                <section className="space-y-4 max-w-2xl">
                    <Link to="/worldcup" className="font-mono text-[10px] uppercase tracking-widest text-white/40 hover:text-white inline-flex items-center gap-1.5">
                        <ArrowLeft size={11} weight="bold" /> Back to WC Hub
                    </Link>
                    <div className="border-2 border-red-500/40 bg-[#0d0d0d] p-6" data-testid="admin-perm-error">
                        <h1 className="font-display font-black text-2xl uppercase">Admin access denied</h1>
                        <p className="font-mono text-[12px] text-white/60 mt-2">{permError}</p>
                    </div>
                </section>
            </AppShell>
        );
    }

    return (
        <AppShell>
            <section className="space-y-5">
                <Link to="/worldcup" className="font-mono text-[10px] uppercase tracking-widest text-white/40 hover:text-white inline-flex items-center gap-1.5">
                    <ArrowLeft size={11} weight="bold" /> Back to WC Hub
                </Link>

                <header className="border-2 border-fa-yellow/40 bg-[#0d0d0d] p-6" data-testid="admin-rc-header">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-fa-yellow flex items-center gap-2">
                        <Sparkle size={11} weight="fill" />
                        Admin · Round Call curator
                    </div>
                    <h1 className="font-display font-black text-2xl md:text-3xl uppercase tracking-tight mt-2">
                        Hand-pick 20 players for the public Round Call
                    </h1>
                    <p className="font-mono text-[11px] text-white/60 mt-2 leading-relaxed">
                        Engine suggests, you decide. Save draft anytime. <span className="text-fa-yellow">Publish</span> replaces the public challenge with your selection. {publishedAt && (
                            <span className="text-fa-mint"> Last published {new Date(publishedAt).toLocaleString()}.</span>
                        )}
                    </p>
                </header>

                {/* Round picker */}
                <div className="flex items-center gap-2 flex-wrap" data-testid="admin-round-tabs">
                    {ROUNDS.map((r) => (
                        <button
                            key={r}
                            type="button"
                            onClick={() => setMatchday(r)}
                            data-testid={`admin-round-${r}`}
                            className={`font-mono text-[11px] uppercase tracking-widest px-3 py-1.5 border ${
                                matchday === r
                                    ? "bg-fa-yellow text-black border-fa-yellow"
                                    : "border-white/15 text-white/60 hover:text-white"
                            }`}
                        >
                            R{r}
                        </button>
                    ))}
                </div>

                {/* Picks tray */}
                <div className="border border-fa-yellow/30 bg-[linear-gradient(135deg,#1a1606,#0d0d0d)] p-4" data-testid="admin-picks-tray">
                    <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
                        <div>
                            <h2 className="font-display font-black text-lg uppercase tracking-tight">
                                Your picks · <span className="text-fa-yellow">{pickIds.length}/{MAX_PICKS}</span>
                            </h2>
                            <p className="font-mono text-[10px] uppercase tracking-widest text-white/40">
                                Order matters — first added shows first.
                            </p>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                onClick={seedFromSuggestion}
                                disabled={suggested.length === 0}
                                className="border border-fa-mint/40 text-fa-mint hover:bg-fa-mint/10 disabled:opacity-30 font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 flex items-center gap-1.5"
                                data-testid="admin-seed-btn"
                                title={`Engine suggests ${suggested.length} top picks for this round`}
                            >
                                <Sparkle size={10} weight="fill" /> Suggest {suggested.length || 20}
                            </button>
                            <button
                                type="button"
                                onClick={clearPicks}
                                disabled={pickIds.length === 0}
                                className="border border-white/15 text-white/60 hover:text-white disabled:opacity-30 font-mono text-[10px] uppercase tracking-widest px-3 py-1.5"
                                data-testid="admin-clear-btn"
                            >
                                Clear
                            </button>
                            <button
                                type="button"
                                onClick={saveDraft}
                                disabled={loadingCuration}
                                className="border border-white/15 text-white/80 hover:bg-white/5 disabled:opacity-30 font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 flex items-center gap-1.5"
                                data-testid="admin-save-btn"
                            >
                                <Check size={10} weight="bold" /> Save draft
                            </button>
                            <button
                                type="button"
                                onClick={publish}
                                disabled={publishing || pickIds.length === 0}
                                className="bg-fa-yellow text-black hover:bg-fa-yellow/90 disabled:opacity-30 font-display font-black uppercase tracking-wide text-xs px-4 py-2 flex items-center gap-1.5"
                                data-testid="admin-publish-btn"
                            >
                                <CloudArrowUp size={12} weight="bold" />
                                {publishing ? "Publishing…" : "Publish live"}
                            </button>
                        </div>
                    </div>
                    {pickedRows.length === 0 ? (
                        <p className="font-mono text-[11px] text-white/40 py-6 text-center">No picks yet. Click <span className="text-fa-mint">Suggest 20</span> to seed, or search below.</p>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-1.5" data-testid="admin-picks-grid">
                            {pickedRows.map(({ id, idx, p }) => (
                                <div
                                    key={id}
                                    className="border border-white/10 bg-[#0d0d0d] p-2 flex items-center gap-2"
                                    data-testid={`admin-pick-${id}`}
                                >
                                    <span className="font-mono text-[9px] tabular-nums text-fa-yellow w-5">#{idx}</span>
                                    <div className="flex-1 min-w-0">
                                        <div className="font-display font-black text-[12px] truncate">{p.webName}</div>
                                        <div className="font-mono text-[9px] uppercase tracking-widest text-white/40">
                                            {p.position || "—"} · {p.nation || "—"}
                                            {p.wcPrice != null && <> · £{p.wcPrice}m</>}
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => setPickIds(pickIds.filter((x) => x !== id))}
                                        className="text-white/40 hover:text-red-400 p-1"
                                        title="Remove"
                                        data-testid={`admin-pick-remove-${id}`}
                                    >
                                        <X size={12} weight="bold" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Browse pool */}
                <div className="border border-white/10 bg-[#0d0d0d]" data-testid="admin-browse">
                    <div className="p-3 border-b border-white/10 flex items-center gap-2 flex-wrap">
                        <div className="relative flex-1 min-w-[180px]">
                            <MagnifyingGlass size={12} weight="bold" className="absolute left-2 top-1/2 -translate-y-1/2 text-white/40" />
                            <input
                                type="text"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search player or nation…"
                                data-testid="admin-search"
                                className="w-full bg-[#0a0a0a] border border-white/15 pl-7 pr-3 py-1.5 font-mono text-[11px] text-white placeholder:text-white/30 focus:outline-none focus:border-fa-yellow"
                            />
                        </div>
                        <select
                            value={position}
                            onChange={(e) => setPosition(e.target.value)}
                            data-testid="admin-pos-filter"
                            className="bg-[#0a0a0a] border border-white/15 px-2 py-1.5 font-mono text-[11px] text-white focus:outline-none focus:border-fa-yellow"
                        >
                            {POSITIONS.map((p) => <option key={p} value={p}>{p || "All positions"}</option>)}
                        </select>
                        <label className="flex items-center gap-1.5 font-mono text-[11px] text-white/60 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={otwOnly}
                                onChange={(e) => setOtwOnly(e.target.checked)}
                                data-testid="admin-otw-filter"
                                className="accent-fa-yellow"
                            />
                            FIFA One-to-Watch only
                        </label>
                        <select
                            value={sort}
                            onChange={(e) => setSort(e.target.value)}
                            data-testid="admin-sort"
                            className="bg-[#0a0a0a] border border-white/15 px-2 py-1.5 font-mono text-[11px] text-white focus:outline-none focus:border-fa-yellow"
                        >
                            <option value="price_desc">Sort: Price ↓</option>
                            <option value="price_asc">Sort: Price ↑</option>
                            <option value="owned_desc">Sort: Owned% ↓</option>
                            <option value="form_desc">Sort: Form ↓</option>
                            <option value="name">Sort: Name</option>
                            <option value="nation">Sort: Nation</option>
                        </select>
                        <span className="font-mono text-[10px] uppercase tracking-widest text-white/40 ml-auto">
                            {loadingBrowse ? "loading…" : `${browseRows.length} of ${browseTotal}`}
                        </span>
                    </div>
                    <div className="divide-y divide-white/5 max-h-[600px] overflow-y-auto">
                        {browseRows.map((p) => {
                            const picked = isPicked(p.playerId);
                            return (
                                <div
                                    key={p.playerId || `${p.fifaFantasyId}`}
                                    className={`flex items-center gap-3 px-3 py-2 hover:bg-white/[0.02] ${picked ? "bg-fa-yellow/5" : ""}`}
                                    data-testid={`admin-row-${p.playerId || p.fifaFantasyId}`}
                                >
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-1.5">
                                            <span className="font-display font-black text-[13px] truncate">{p.webName}</span>
                                            {p.wcOneToWatch && <span className="font-mono text-[8px] uppercase tracking-widest px-1 border border-fa-yellow/60 text-fa-yellow">OTW</span>}
                                        </div>
                                        <div className="font-mono text-[10px] uppercase tracking-widest text-white/40">
                                            {p.position || "—"} · {p.nation || "—"}
                                            {p.wcPrice != null && <> · <span className="text-fa-yellow">£{p.wcPrice}m</span></>}
                                            {p.wcSelectedPct != null && <> · own {p.wcSelectedPct.toFixed(1)}%</>}
                                            {p.form > 0 && <> · form {p.form.toFixed(1)}</>}
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => togglePick(p)}
                                        data-testid={`admin-toggle-${p.playerId || p.fifaFantasyId}`}
                                        className={`font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 border flex items-center gap-1 ${
                                            picked
                                                ? "border-fa-mint text-fa-mint bg-fa-mint/10"
                                                : "border-white/15 text-white/60 hover:text-white hover:border-white/30"
                                        }`}
                                    >
                                        {picked ? <><Check size={10} weight="bold" /> Added</> : <><Plus size={10} weight="bold" /> Add</>}
                                    </button>
                                </div>
                            );
                        })}
                        {!loadingBrowse && browseRows.length === 0 && (
                            <p className="font-mono text-[11px] text-white/40 py-8 text-center">No players match filters.</p>
                        )}
                    </div>
                </div>
            </section>
        </AppShell>
    );
}
